import { useEffect, useMemo, useState } from "react";

/**
 * 084c -- Half-aware match clock with progress bar.
 *
 * Drives phases purely from the match's scheduled time + half lengths
 * (no referee whistle signal yet). Shows:
 *   - PRE-MATCH (before starts_at)
 *   - 1ST HALF (elapsed/remaining, pulsing progress)
 *   - HALF TIME (paused, break countdown)
 *   - 2ND HALF
 *   - FULL TIME / FINAL once everything is done
 *
 * Overrun: when the actual time exceeds the scheduled end of a phase, the
 * bar stays full and an orange "+X'" injury-time indicator appears. The
 * "proper" fix (ref taps an End Half button to advance the clock) would
 * need DB columns + new UI -- tracked as future work.
 */

type Phase = "pre" | "h1" | "break" | "h2" | "full";

interface ClockProps {
  startsAt: string;
  firstHalfMins: number;
  breakMins: number;
  secondHalfMins: number;
  /** true if the match is finalised (overrides clock with "FINAL"). */
  final: boolean;
  /** Compact variant (smaller text + padding) for tight spaces like schedule cards. */
  compact?: boolean;
}

export function MatchClock({ startsAt, firstHalfMins, breakMins, secondHalfMins, final, compact = false }: ClockProps) {
  const [now, setNow] = useState(Date.now());
  const [showRemaining, setShowRemaining] = useState(false);

  const startsMs = useMemo(() => new Date(startsAt).getTime(), [startsAt]);
  const h1EndsMs = startsMs + firstHalfMins * 60_000;
  const breakEndsMs = h1EndsMs + breakMins * 60_000;
  const h2EndsMs = breakEndsMs + secondHalfMins * 60_000;

  const phase: Phase = final
    ? "full"
    : now < startsMs
    ? "pre"
    : now < h1EndsMs
    ? "h1"
    : now < breakEndsMs
    ? "break"
    : now < h2EndsMs
    ? "h2"
    : "full";

  // Re-render every second except during pre-match / full-time, where we
  // can sleep longer until the next phase boundary.
  useEffect(() => {
    if (phase === "full" || final) return;
    const t = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(t);
  }, [phase, final]);

  if (final) {
    return (
      <ClockShell phaseLabel="FINAL" barPct={100} barClass="bg-muted-foreground/40" label="--" compact={compact} />
    );
  }

  if (phase === "pre") {
    return (
      <ClockShell
        phaseLabel="KICK-OFF IN"
        barPct={0}
        barClass="bg-muted"
        label={fmtCountdown(startsMs - now)}
        compact={compact}
      />
    );
  }

  if (phase === "full") {
    return (
      <ClockShell phaseLabel="FULL TIME" barPct={100} barClass="bg-muted-foreground/40" label="--" compact={compact} />
    );
  }

  // Active phase: compute elapsed / remaining inside this phase.
  const phaseMeta = phase === "h1"
    ? { name: "1ST HALF", phaseStart: startsMs, phaseEnd: h1EndsMs, nominalMins: firstHalfMins }
    : phase === "break"
    ? { name: "HALF TIME", phaseStart: h1EndsMs, phaseEnd: breakEndsMs, nominalMins: breakMins }
    : { name: "2ND HALF", phaseStart: breakEndsMs, phaseEnd: h2EndsMs, nominalMins: secondHalfMins };

  const elapsedMs = now - phaseMeta.phaseStart;
  const nominalMs = phaseMeta.nominalMins * 60_000;
  const clampedElapsed = Math.min(elapsedMs, nominalMs);
  const overrunMs = Math.max(0, elapsedMs - nominalMs);
  const remainingMs = Math.max(0, nominalMs - elapsedMs);
  const barPct = Math.min(100, (clampedElapsed / nominalMs) * 100);

  const barClass = phase === "break"
    ? "bg-amber-500/70"
    : "bg-green-500/80";

  const label = showRemaining
    ? fmtMmSs(remainingMs)
    : fmtMmSs(clampedElapsed);

  return (
    <ClockShell
      phaseLabel={phase === "break" ? "HALF TIME" : phaseMeta.name}
      barPct={barPct}
      barClass={barClass}
      label={label}
      overrunMins={overrunMs > 0 ? Math.ceil(overrunMs / 60_000) : 0}
      onToggle={() => setShowRemaining((v) => !v)}
      paused={phase === "break"}
      compact={compact}
    />
  );
}

function ClockShell({
  phaseLabel,
  barPct,
  barClass,
  label,
  overrunMins = 0,
  onToggle,
  paused = false,
  compact = false,
}: {
  phaseLabel: string;
  barPct: number;
  barClass: string;
  label: string;
  overrunMins?: number;
  onToggle?: () => void;
  paused?: boolean;
  compact?: boolean;
}) {
  const Wrapper = onToggle ? "button" : "div";
  return (
    <Wrapper
      type={onToggle ? "button" : undefined}
      onClick={onToggle}
      className={`relative flex w-full items-center overflow-hidden rounded border bg-muted/30 ${onToggle ? "cursor-pointer" : ""}`}
      title={onToggle ? "Tap to toggle elapsed / remaining" : undefined}
      data-field="match-clock"
    >
      <div
        className={`absolute inset-y-0 left-0 transition-[width] duration-500 ${barClass} ${paused ? "opacity-60" : ""}`}
        style={{ width: `${barPct}%` }}
        aria-hidden
      />
      <div className={`relative flex w-full items-center justify-between ${compact ? "px-1.5 py-0.5 text-[10px]" : "px-3 py-2 text-sm"}`}>
        <span className={`${compact ? "text-[9px]" : "text-xs"} font-semibold uppercase tracking-wide text-foreground/90`}>{phaseLabel}</span>
        <span className={`flex items-center gap-1 font-mono ${compact ? "text-[11px]" : "text-base"} font-semibold tabular-nums`}>
          {label}
          {overrunMins > 0 && (
            <span className={`rounded bg-orange-500 ${compact ? "px-0.5 text-[8px]" : "px-1 py-0.5 text-[10px]"} font-bold uppercase leading-none text-white`}>
              +{overrunMins}&apos;
            </span>
          )}
        </span>
      </div>
    </Wrapper>
  );
}

/** Countdown formatter for pre-match. Scales up as the match is further
 *  away so the number stays readable:
 *    < 1h   →  M:SS        (e.g. 12:34)
 *    < 1d   →  H:MM:SS     (e.g. 3:07:22)
 *    >= 1d  →  Xd HH:MM    (e.g. 2d 04:17)
 */
function fmtCountdown(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1_000));
  const d = Math.floor(totalSec / 86_400);
  const h = Math.floor((totalSec % 86_400) / 3_600);
  const m = Math.floor((totalSec % 3_600) / 60);
  const s = totalSec % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  if (d > 0) return `${d}d ${pad(h)}:${pad(m)}`;
  if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
  return `${m}:${pad(s)}`;
}

function fmtMmSs(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1_000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
